import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { getFullImageUrl, handleImageError } from '../../config/environment';
import './SliderManagement.css';

const SliderManagement = () => {
  const { t } = useTranslation();
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSlider, setEditingSlider] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    linkUrl: '',
    linkText: '',
    image: null,
    imagePreview: null,
    isActive: true,
    displayOrder: 1,
    language: 'all'
  });
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadSliders();
  }, []);

  useEffect(() => {
    if (message && message.type === 'success') {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const loadSliders = async () => {
    setLoading(true);
    try {
      const data = await api.getSliders();
      setSliders(data || []);
    } catch (error) {
      console.error('Error loading sliders:', error);
      setMessage({ 
        type: 'error', 
        text: t('slider.loadError') || 'Failed to load sliders'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: t('slider.imageSizeError') });
        return;
      }

      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setMessage({ type: 'error', text: t('slider.imageTypeError') });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          image: file,
          imagePreview: reader.result
        }));
        setMessage(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: t('slider.titleRequired') });
      return false;
    }
    if (!formData.subtitle.trim()) {
      setMessage({ type: 'error', text: t('slider.subtitleRequired') });
      return false;
    }
    if (!formData.linkUrl.trim()) {
      setMessage({ type: 'error', text: t('slider.linkUrlRequired') });
      return false;
    }
    if (!formData.linkText.trim()) {
      setMessage({ type: 'error', text: t('slider.linkTextRequired') });
      return false;
    }
    if (!editingSlider && !formData.image) {
      setMessage({ type: 'error', text: t('slider.imageRequired') });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setMessage(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('subtitle', formData.subtitle.trim());
      formDataToSend.append('linkUrl', formData.linkUrl.trim());
      formDataToSend.append('linkText', formData.linkText.trim());
      formDataToSend.append('isActive', formData.isActive);
      formDataToSend.append('displayOrder', parseInt(formData.displayOrder));
      formDataToSend.append('language', formData.language || 'all');
      
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      if (editingSlider) {
        await api.updateSlider(editingSlider.id, formDataToSend);
        setMessage({ type: 'success', text: t('slider.updateSuccess') });
      } else {
        await api.createSlider(formDataToSend);
        setMessage({ type: 'success', text: t('slider.createSuccess') });
      }

      setShowModal(false);
      resetForm();
      await loadSliders();
    } catch (error) {
      console.error('Error saving slider:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || t('slider.saveError')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (slider) => {
    setEditingSlider(slider);
    setFormData({
      title: slider.title || '',
      subtitle: slider.subtitle || '',
      linkUrl: slider.linkUrl || '',
      linkText: slider.linkText || '',
      image: null,
      imagePreview: slider.imageUrl || null,
      isActive: slider.isActive,
      displayOrder: slider.displayOrder || 1,
      language: slider.language || 'all'
    });
    setMessage(null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const slider = sliders.find(s => s.id === id);
    const confirmMessage = t('slider.deleteConfirm', { title: slider?.title });
    
    if (!window.confirm(confirmMessage)) return;

    setLoading(true);
    try {
      await api.deleteSlider(id);
      setMessage({ type: 'success', text: t('slider.deleteSuccess') });
      await loadSliders();
    } catch (error) {
      console.error('Error deleting slider:', error);
      setMessage({ type: 'error', text: t('slider.deleteError') });
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    setLoading(true);
    try {
      await api.toggleSliderStatus(id);
      setMessage({ 
        type: 'success', 
        text: currentStatus ? t('slider.deactivateSuccess') : t('slider.activateSuccess')
      });
      await loadSliders();
    } catch (error) {
      console.error('Error toggling status:', error);
      setMessage({ type: 'error', text: t('slider.statusError') });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      linkUrl: '',
      linkText: '',
      image: null,
      imagePreview: null,
      isActive: true,
      displayOrder: 1,
      language: 'all'
    });
    setEditingSlider(null);
    setMessage(null);
  };

  const openModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const getLanguageBadge = (language) => {
    const lang = language || 'all';
    const badges = {
      'all': { label: t('slider.allLanguages'), icon: '🌐', color: '#6366f1' },
      'en': { label: t('slider.english'), icon: '🇬🇧', color: '#3b82f6' },
      'hi': { label: t('slider.hindi'), icon: '🇮🇳', color: '#f59e0b' }
    };
    return badges[lang.toLowerCase()] || badges['all'];
  };

  return (
    <div className="slider-management-container">
      <div className="slider-header">
        <h2>
          <i className="fas fa-images"></i>
          {t('slider.title')}
        </h2>
        <button onClick={openModal} className="add-btn" disabled={loading}>
          <i className="fas fa-plus"></i>
          {t('slider.addNew')}
        </button>
      </div>

      {message && (
        <div className={`alert ${message.type}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)} className="close-alert">×</button>
        </div>
      )}

      {loading && !showModal ? (
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>{t('slider.loading')}</p>
        </div>
      ) : (
        <div className="sliders-grid">
          {sliders.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-images"></i>
              <h3>{t('slider.noSliders')}</h3>
              <p>{t('slider.noSlidersDescription')}</p>
              <button onClick={openModal} className="empty-state-btn">
                <i className="fas fa-plus-circle"></i>
                {t('slider.createFirst')}
              </button>
            </div>
          ) : (
            sliders.map((slider) => {
              const langBadge = getLanguageBadge(slider.language);
              return (
                <div key={slider.id} className="slider-card">
                  <div className="slider-image">
                    <img 
                      src={getFullImageUrl(slider.imageUrl)}
                      alt={slider.title}
                      onError={handleImageError}
                      loading="lazy"
                    />
                    <div className="slider-overlay">
                      <div className="slider-info">
                        <h3>{slider.title}</h3>
                        <p>{slider.subtitle}</p>
                      </div>
                    </div>
                  </div>
                  <div className="slider-content">
                    <div className="slider-details">
                      <span className="link-info" title={slider.linkUrl}>
                        <i className="fas fa-link"></i>
                        {slider.linkUrl}
                      </span>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span className="order-badge">{t('slider.order')}: {slider.displayOrder}</span>
                        <span 
                          className="order-badge" 
                          style={{ background: langBadge.color, color: 'white' }}
                          title={langBadge.label}
                        >
                          {langBadge.icon} {langBadge.label}
                        </span>
                      </div>
                    </div>
                    <div className="slider-actions">
                      <button
                        onClick={() => toggleStatus(slider.id, slider.isActive)}
                        className={`status-btn ${slider.isActive ? 'active' : 'inactive'}`}
                        disabled={loading}
                      >
                        <i className={`fas fa-${slider.isActive ? 'eye' : 'eye-slash'}`}></i>
                        {slider.isActive ? t('slider.active') : t('slider.inactive')}
                      </button>
                      <button onClick={() => handleEdit(slider)} className="edit-btn" disabled={loading}>
                        <i className="fas fa-edit"></i>
                        {t('slider.edit')}
                      </button>
                      <button onClick={() => handleDelete(slider.id)} className="delete-btn" disabled={loading}>
                        <i className="fas fa-trash"></i>
                        {t('slider.delete')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i className="fas fa-image"></i>
                {editingSlider ? t('slider.editTitle') : t('slider.addTitle')}
              </h3>
              <button onClick={closeModal} className="close-modal" disabled={loading}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="slider-form">
              <div className="form-section">
                <h4><i className="fas fa-info-circle"></i> {t('slider.basicInfo')}</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="title">{t('slider.titleLabel')} *</label>
                    <input
                      id="title"
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      placeholder={t('slider.titlePlaceholder')}
                      maxLength={200}
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="subtitle">{t('slider.subtitleLabel')} *</label>
                    <input
                      id="subtitle"
                      type="text"
                      name="subtitle"
                      value={formData.subtitle}
                      onChange={handleChange}
                      required
                      placeholder={t('slider.subtitlePlaceholder')}
                      maxLength={500}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="linkUrl">{t('slider.linkUrlLabel')} *</label>
                    <input
                      id="linkUrl"
                      type="text"
                      name="linkUrl"
                      value={formData.linkUrl}
                      onChange={handleChange}
                      required
                      placeholder={t('slider.linkUrlPlaceholder')}
                      maxLength={500}
                      disabled={loading}
                    />
                    <small>{t('slider.linkUrlHelper')}</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="linkText">{t('slider.linkTextLabel')} *</label>
                    <input
                      id="linkText"
                      type="text"
                      name="linkText"
                      value={formData.linkText}
                      onChange={handleChange}
                      required
                      placeholder={t('slider.linkTextPlaceholder')}
                      maxLength={100}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="displayOrder">{t('slider.displayOrderLabel')} *</label>
                    <input
                      id="displayOrder"
                      type="number"
                      name="displayOrder"
                      value={formData.displayOrder}
                      onChange={handleChange}
                      min="1"
                      max="100"
                      required
                      disabled={loading}
                    />
                    <small>{t('slider.displayOrderHelper')}</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="language">{t('slider.languageLabel')} *</label>
                    <select
                      id="language"
                      name="language"
                      value={formData.language}
                      onChange={handleChange}
                      disabled={loading}
                      style={{
                        padding: '12px 15px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '15px'
                      }}
                    >
                      <option value="all">🌐 {t('slider.allLanguages')}</option>
                      <option value="en">🇬🇧 {t('slider.english')}</option>
                      <option value="hi">🇮🇳 {t('slider.hindi')}</option>
                    </select>
                    <small>{t('slider.languageHelper')}</small>
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    <span>{t('slider.activeLabel')}</span>
                  </label>
                </div>
              </div>

              <div className="form-section">
                <h4><i className="fas fa-camera"></i> {t('slider.imageSection')} {!editingSlider && '*'}</h4>
                
                <div className="image-upload-section">
                  {formData.imagePreview ? (
                    <div className="image-preview">
                      <img 
                        src={
                          formData.imagePreview.startsWith('data:') 
                            ? formData.imagePreview 
                            : getFullImageUrl(formData.imagePreview)
                        }
                        alt="Preview"
                        onError={handleImageError}
                      />
                      <button
                        type="button"
                        className="change-image-btn"
                        onClick={() => document.getElementById('slider-image-input').click()}
                        disabled={loading}
                      >
                        <i className="fas fa-camera"></i>
                        {t('slider.changeImage')}
                      </button>
                      {editingSlider && (
                        <p className="image-note" style={{ marginTop: '10px', color: '#666' }}>
                          <i className="fas fa-info-circle"></i>
                          {t('slider.imageNote')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div 
                      className="image-upload-box"
                      onClick={() => document.getElementById('slider-image-input').click()}
                    >
                      <i className="fas fa-cloud-upload-alt"></i>
                      <p>{t('slider.clickToUpload')}</p>
                      <small>{t('slider.imageSpecs')}</small>
                    </div>
                  )}

                  <input
                    id="slider-image-input"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={closeModal} className="cancel-btn" disabled={loading}>
                  <i className="fas fa-times"></i>
                  {t('slider.cancel')}
                </button>
                <button type="submit" disabled={loading} className="submit-btn">
                  {loading ? (
                    <>
                      <div className="spinner-small"></div>
                      {t('slider.saving')}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      {editingSlider ? t('slider.update') : t('slider.create')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SliderManagement;
